import { useMemo } from "react";
import { Table, Spinner } from "reactstrap";
import { withNamespaces } from "react-i18next";
import { Accordion } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { EMPTY_LIST } from "src/common/constants";
import KYBService from "src/modules/3rd-party-management/apis/admin/KYBService";
import LegalEntitiesImage from "src/modules/3rd-party-management/assets/images/legal-entities.svg";
import {
    extractSubsidiaries,
    formatEntityTypeLabel,
    formatRelationTypeLabel,
} from "src/modules/3rd-party-management/utils/shareholderGraph";

const Subsidiaries = ({
    t,
    activeAccordion,
    setActiveAccordion,
    detailsQuery,
}) => {
    const idInSource = detailsQuery.data?.company?.idInSource;

    const {
        data: shareholdersGraph,
        isFetching: fetchingSubsidiaries,
    } = useQuery({
        queryKey: [
            "3rd-party-management-kyb-get-organization-details",
            "shareholders-graph",
            idInSource,
        ],
        queryFn: async () => {
            const service = KYBService.getInstance();
            return await service.getShareholdersGraph(idInSource, {});
        },
        cacheTime: 10,
        refetchOnWindowFocus: false,
        enabled: !!idInSource,
        retry: 0,
        onError: () => {
            toast(t("Error"), {
                type: "error",
            });
        },
    });

    const subsidiaries = useMemo(
        () => extractSubsidiaries(shareholdersGraph),
        [shareholdersGraph]
    );

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion("subsidiaries")}
            className={activeAccordion === "subsidiaries" ? "active" : ""}
            eventKey="subsidiaries"
        >
            <Accordion.Header>
                <img src={LegalEntitiesImage} alt="" />
                <span>{t("Subsidiaries")}</span>
            </Accordion.Header>

            <Accordion.Body>
                <div className="shareholders-section mb-5">
                    <Table striped size="sm" aria-label={t("Subsidiaries table")}>
                        <caption className="visually-hidden">
                            {t("List of subsidiaries related to this organization.")}
                        </caption>
                        <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 2,
                            }}
                        >
                            <tr>
                                <th scope="col">{t("Name")}</th>
                                <th scope="col">{t("Type")}</th>
                                <th scope="col">{t("Relation")}</th>
                                <th scope="col">{t("Percentage")}</th>
                                <th scope="col">{t("Address")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fetchingSubsidiaries ? (
                                <tr>
                                    <td colSpan={5} className="text-center">
                                        <Spinner size="sm" className="me-2" />
                                        {t("Fetching")}...
                                    </td>
                                </tr>
                            ) : subsidiaries.length > 0 ? (
                                subsidiaries.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.name}</td>
                                        <td>
                                            {formatEntityTypeLabel(item.entityType, t) || "—"}
                                        </td>
                                        <td>
                                            {formatRelationTypeLabel(
                                                item.relationType,
                                                t
                                            ) || "—"}
                                        </td>
                                        <td>
                                            {typeof item.percentage === "number"
                                                ? `${item.percentage.toFixed(2)}%`
                                                : "—"}
                                        </td>
                                        <td>{item.address || "—"}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="text-center">
                                        {t(EMPTY_LIST)}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default withNamespaces()(Subsidiaries);
